import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { compagnieService, CreateCompagnieDTO, UpdateCompagnieDTO, ImportResult } from '@/services/compagnieService';
import type { Compagnie } from '@/services/compagnieService';
import toast from 'react-hot-toast';
import { getErrorMessage } from '@/services/api';
import { ConfirmModal } from '@/components/common/Modal';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOffice2Icon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';

/**
 * Page de gestion des compagnies aeriennes
 */
export function CompagniesPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingCompagnie, setEditingCompagnie] = useState<Compagnie | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Compagnie | null>(null);
  const [toggleTarget, setToggleTarget] = useState<Compagnie | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Chargement des compagnies (toutes, actives + inactives)
  const { data: compagnies, isLoading } = useQuery({
    queryKey: ['compagnies', 'all'],
    queryFn: () => compagnieService.list(true),
  });

  // Stats
  const { data: stats } = useQuery({
    queryKey: ['compagnies-stats'],
    queryFn: () => compagnieService.getStats(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateCompagnieDTO) => compagnieService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compagnies'] });
      queryClient.invalidateQueries({ queryKey: ['compagnies-stats'] });
      setShowModal(false);
      toast.success('Compagnie creee avec succes');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCompagnieDTO }) =>
      compagnieService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compagnies'] });
      queryClient.invalidateQueries({ queryKey: ['compagnies-stats'] });
      setShowModal(false);
      setEditingCompagnie(null);
      toast.success('Compagnie modifiee avec succes');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => compagnieService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compagnies'] });
      queryClient.invalidateQueries({ queryKey: ['compagnies-stats'] });
      toast.success('Compagnie supprimee');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const toggleActifMutation = useMutation({
    mutationFn: (id: number) => compagnieService.toggleActif(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['compagnies'] });
      queryClient.invalidateQueries({ queryKey: ['compagnies-stats'] });
      toast.success(data.actif ? 'Compagnie reactivee' : 'Compagnie desactivee');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => compagnieService.import(file),
    onSuccess: (data) => {
      setImportResult(data);
      setImportFile(null);
      queryClient.invalidateQueries({ queryKey: ['compagnies'] });
      queryClient.invalidateQueries({ queryKey: ['compagnies-stats'] });
      const msg = `Import termine : ${data.created} creee(s), ${data.updated} mise(s) a jour`;
      if (data.errors.length > 0) {
        toast.success(msg + ` (${data.errors.length} erreur(s))`);
      } else {
        toast.success(msg);
      }
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const handleImport = () => {
    if (importFile) {
      importMutation.mutate(importFile);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const codeIata = (formData.get('codeIata') as string).toUpperCase().trim();
    const nom = (formData.get('nom') as string).trim();

    if (editingCompagnie) {
      updateMutation.mutate({ id: editingCompagnie.id, data: { codeIata, nom } });
    } else {
      createMutation.mutate({ codeIata, nom });
    }
  };

  const handleDelete = (compagnie: Compagnie) => {
    setDeleteTarget(compagnie);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const handleToggle = (compagnie: Compagnie) => {
    setToggleTarget(compagnie);
  };

  const confirmToggle = () => {
    if (toggleTarget) {
      toggleActifMutation.mutate(toggleTarget.id);
      setToggleTarget(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement des compagnies...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Compagnies aeriennes</h2>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => {
              setImportFile(null);
              setImportResult(null);
              setShowImportModal(true);
            }}
            className="btn-secondary"
          >
            <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
            Importer
          </button>
          <button
            onClick={() => {
              setEditingCompagnie(null);
              setShowModal(true);
            }}
            className="btn-primary"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Ajouter une compagnie
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Total</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-green-600">{stats.actives}</p>
              <p className="text-sm text-gray-500">Actives</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-red-600">{stats.inactives}</p>
              <p className="text-sm text-gray-500">Inactives</p>
            </div>
          </div>
        </div>
      )}

      {/* Tableau */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Code IATA</th>
                <th>Nom</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {compagnies?.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-12">
                    <BuildingOffice2Icon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 mb-3">Aucune compagnie trouvee</p>
                    <button
                      onClick={() => { setEditingCompagnie(null); setShowModal(true); }}
                      className="btn-primary inline-flex items-center"
                    >
                      <PlusIcon className="w-5 h-5 mr-2" />
                      Ajouter une compagnie
                    </button>
                  </td>
                </tr>
              ) : (
                compagnies?.map((compagnie) => (
                  <tr key={compagnie.id}>
                    <td className="font-medium font-mono">{compagnie.codeIata}</td>
                    <td>{compagnie.nom}</td>
                    <td>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          compagnie.actif
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {compagnie.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setEditingCompagnie(compagnie);
                            setShowModal(true);
                          }}
                          className="p-1 text-gray-400 hover:text-primary-500"
                          title="Modifier"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleToggle(compagnie)}
                          className={`px-2 py-1 text-xs rounded ${
                            compagnie.actif
                              ? 'text-orange-600 hover:bg-orange-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={compagnie.actif ? 'Desactiver' : 'Reactiver'}
                        >
                          {compagnie.actif ? 'Desactiver' : 'Reactiver'}
                        </button>
                        <button
                          onClick={() => handleDelete(compagnie)}
                          className="p-1 text-gray-400 hover:text-red-500"
                          title="Supprimer"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modale confirmation suppression */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={`Supprimer la compagnie ${deleteTarget?.codeIata} - ${deleteTarget?.nom} ?`}
        message="Cette action est irreversible. La compagnie sera definitivement supprimee."
        confirmLabel="Supprimer"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Modale confirmation toggle actif */}
      <ConfirmModal
        isOpen={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={confirmToggle}
        title={toggleTarget?.actif ? 'Desactiver la compagnie ?' : 'Reactiver la compagnie ?'}
        message={toggleTarget?.actif
          ? `La compagnie "${toggleTarget.codeIata} - ${toggleTarget.nom}" ne sera plus disponible pour les nouvelles interventions.`
          : `La compagnie "${toggleTarget?.codeIata} - ${toggleTarget?.nom}" sera de nouveau disponible pour les nouvelles interventions.`
        }
        confirmLabel={toggleTarget?.actif ? 'Desactiver' : 'Reactiver'}
        variant="warning"
        isLoading={toggleActifMutation.isPending}
      />

      {/* Modal creation/edition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingCompagnie ? 'Modifier la compagnie' : 'Nouvelle compagnie'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Code IATA *</label>
                <input
                  type="text"
                  name="codeIata"
                  defaultValue={editingCompagnie?.codeIata}
                  className="input uppercase"
                  required
                  maxLength={3}
                  placeholder="Ex: AF"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
              <div>
                <label className="label">Nom *</label>
                <input
                  type="text"
                  name="nom"
                  defaultValue={editingCompagnie?.nom}
                  className="input"
                  required
                  placeholder="Ex: Air France"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCompagnie(null);
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingCompagnie ? 'Modifier' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal import Excel */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Importer des compagnies</h3>

            {!importResult ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Fichier Excel avec colonnes : <strong>Code IATA</strong> | <strong>Nom</strong>
                </p>
                <div>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowImportModal(false)}
                    className="btn-secondary"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={!importFile || importMutation.isPending}
                    className="btn-primary"
                  >
                    {importMutation.isPending ? 'Import en cours...' : 'Importer'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{importResult.created}</p>
                    <p className="text-sm text-green-700">Creee(s)</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{importResult.updated}</p>
                    <p className="text-sm text-blue-700">Mise(s) a jour</p>
                  </div>
                </div>
                {importResult.errors.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-sm font-semibold text-red-700 mb-2">
                      {importResult.errors.length} erreur(s) :
                    </p>
                    <ul className="text-sm text-red-600 space-y-1 max-h-40 overflow-y-auto">
                      {importResult.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="btn-primary"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CompagniesPage;
